import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { fetchTraineesByManagerId, getBatchesByManager } from '../../../api_service';

const blankQuestion = (type = 'MCQ') => ({
  questionType: type,
  questionText: '',
  marks: 1,
  options: type === 'MCQ' ? ['', ''] : [],
  correctIndex: 0,
  correctAnswer: '',
});

const CreateQuizModal = ({ managerId, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    deadline: '',
    timeLimitMinutes: 30,
  });
  const [questions, setQuestions] = useState([blankQuestion('MCQ')]);
  const [trainees, setTrainees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchTraineesByManagerId(managerId);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setTrainees(list);
      } catch {
        setTrainees([]);
      }
      try {
        const res = await getBatchesByManager(managerId);
        setBatches(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setBatches([]);
      }
    };
    load();
  }, [managerId]);

  const toggleTrainee = (userid) => {
    setSelectedIds(prev =>
      prev.includes(userid) ? prev.filter(x => x !== userid) : [...prev, userid]
    );
  };

  const applyBatch = (batchId) => {
    setSelectedBatch(batchId);
    if (!batchId) return;
    const b = batches.find(x => String(x.batchId) === String(batchId));
    if (!b || !Array.isArray(b.members)) return;
    const ids = b.members.map(m => m.userid);
    setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
  };

  const updateQuestion = (idx, patch) => {
    setQuestions(prev => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const setType = (idx, type) => {
    updateQuestion(idx, {
      questionType: type,
      options: type === 'MCQ' ? ['', ''] : [],
      correctIndex: 0,
      correctAnswer: '',
    });
  };

  const addOption = (idx) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== idx || q.options.length >= 6) return q;
      return { ...q, options: [...q.options, ''] };
    }));
  };

  const removeOption = (idx, optIdx) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== idx || q.options.length <= 2) return q;
      const options = q.options.filter((_, oi) => oi !== optIdx);
      let correctIndex = q.correctIndex;
      if (optIdx === correctIndex) correctIndex = 0;
      else if (optIdx < correctIndex) correctIndex -= 1;
      return { ...q, options, correctIndex };
    }));
  };

  const updateOption = (idx, optIdx, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== idx) return q;
      const options = q.options.map((o, oi) => (oi === optIdx ? value : o));
      return { ...q, options };
    }));
  };

  const addQuestion = () => setQuestions(prev => [...prev, blankQuestion('MCQ')]);
  const removeQuestion = (idx) =>
    setQuestions(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.deadline) { setError('Deadline is required'); return; }
    if (questions.length === 0) { setError('Add at least one question'); return; }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.questionText.trim()) { setError(`Question ${i + 1}: text is required`); return; }
      if (q.questionType === 'MCQ') {
        if (q.options.some(o => !o.trim())) { setError(`Question ${i + 1}: fill all options`); return; }
        if (q.correctIndex == null || !q.options[q.correctIndex]) { setError(`Question ${i + 1}: mark a correct option`); return; }
      } else if (!q.correctAnswer.trim()) {
        setError(`Question ${i + 1}: correct answer is required`); return;
      }
    }
    if (selectedIds.length === 0) { setError('Select at least one trainee'); return; }

    const payloadQuestions = questions.map(q => ({
      questionText: q.questionText,
      questionType: q.questionType,
      optionsJson: q.questionType === 'MCQ' ? JSON.stringify(q.options) : null,
      correctAnswer: q.questionType === 'MCQ' ? q.options[q.correctIndex] : q.correctAnswer,
      marks: Number(q.marks) || 1,
    }));

    setLoading(true);
    setError('');
    try {
      await onCreated({
        title: form.title,
        description: form.description,
        deadline: form.deadline,
        timeLimitMinutes: Number(form.timeLimitMinutes) || null,
        traineeIds: selectedIds,
        questions: payloadQuestions,
      });
      onClose();
    } catch {
      setError('Failed to create quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Create New Quiz</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 flex-1">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Quiz Title *</label>
            <Input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Finacle Basics Assessment"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows={3}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the assessment and instructions..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Deadline *</label>
              <Input
                type="date" min={new Date().toISOString().split("T")[0]}
                value={form.deadline}
                onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Time Limit (minutes)</label>
              <Input
                type="number"
                value={form.timeLimitMinutes}
                onChange={e => setForm(f => ({ ...f, timeLimitMinutes: Number(e.target.value) }))}
                min={1}
              />
            </div>
          </div>

          {/* Questions builder */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-foreground">
                Questions <span className="text-muted-foreground font-normal">({questions.length})</span>
              </label>
              <Button size="sm" variant="outline" onClick={addQuestion} iconName="Plus" iconSize={14}>
                Add Question
              </Button>
            </div>

            <div className="space-y-4">
              {questions.map((q, idx) => (
                <div key={idx} className="border border-border rounded-lg p-4 space-y-3 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Question {idx + 1}</span>
                    <div className="flex items-center gap-2">
                      <div className="flex rounded-md border border-border overflow-hidden text-xs">
                        <button
                          type="button"
                          onClick={() => setType(idx, 'MCQ')}
                          className={`px-3 py-1 ${q.questionType === 'MCQ' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground'}`}
                        >
                          MCQ
                        </button>
                        <button
                          type="button"
                          onClick={() => setType(idx, 'FILL_BLANK')}
                          className={`px-3 py-1 ${q.questionType === 'FILL_BLANK' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground'}`}
                        >
                          Fill-in-blank
                        </button>
                      </div>
                      {questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeQuestion(idx)}
                          className="text-muted-foreground hover:text-error"
                          title="Remove question"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <textarea
                    className="w-full border border-border rounded-lg p-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    rows={2}
                    value={q.questionText}
                    onChange={e => updateQuestion(idx, { questionText: e.target.value })}
                    placeholder="Enter the question text..."
                  />

                  {q.questionType === 'MCQ' ? (
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`correct-${idx}`}
                            checked={q.correctIndex === oi}
                            onChange={() => updateQuestion(idx, { correctIndex: oi })}
                            className="accent-primary"
                            title="Mark as correct"
                          />
                          <input
                            className="flex-1 border border-border rounded-md px-3 py-1.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            value={opt}
                            onChange={e => updateOption(idx, oi, e.target.value)}
                            placeholder={`Option ${oi + 1}`}
                          />
                          {q.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeOption(idx, oi)}
                              className="text-muted-foreground hover:text-error"
                            >
                              <Icon name="X" size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                      {q.options.length < 6 && (
                        <button
                          type="button"
                          onClick={() => addOption(idx)}
                          className="text-xs text-primary hover:underline"
                        >
                          + Add option
                        </button>
                      )}
                      <p className="text-xs text-muted-foreground">Select the radio next to the correct option.</p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Correct Answer</label>
                      <input
                        className="w-full border border-border rounded-md px-3 py-1.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        value={q.correctAnswer}
                        onChange={e => updateQuestion(idx, { correctAnswer: e.target.value })}
                        placeholder="Expected answer (case-insensitive match)"
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-foreground">Marks</label>
                    <input
                      type="number"
                      className="w-20 border border-border rounded-md px-2 py-1 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      value={q.marks}
                      min={1}
                      onChange={e => updateQuestion(idx, { marks: Number(e.target.value) })}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trainee selection */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-foreground mb-1">Quick-add from batch</label>
            <select
              value={selectedBatch}
              onChange={e => applyBatch(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Select a batch to add its trainees…</option>
              {batches.map(b => (
                <option key={b.batchId} value={b.batchId}>
                  {b.name} ({Array.isArray(b.members) ? b.members.length : 0})
                </option>
              ))}
            </select>
            {batches.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">No batches yet — create one in Batches.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Assign Trainees * <span className="text-muted-foreground font-normal">({selectedIds.length} selected)</span>
            </label>
            <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
              {trainees.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No trainees found</p>
              ) : (
                trainees.map(t => (
                  <label key={t.userid} className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(t.userid)}
                      onChange={() => toggleTrainee(t.userid)}
                      className="accent-primary"
                    />
                    <span className="text-sm text-foreground">{t.firstname} {t.lastname}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{t.emailid}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Quiz'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateQuizModal;
